<template>
  <div
    class="explanations-carousel tw-relative tw-z-0 tw-w-full"
    :class="locale === 'he' ? 'tw-font-heebo' : ''"
    @mouseenter="pauseAutoplay"
    @mouseleave="resumeAutoplay"
  >
    <!-- Prev arrow — hidden on mobile -->
    <button
      type="button"
      class="carousel-arrow carousel-arrow-prev tw-hidden md:tw-inline-flex"
      :aria-label="$l('carouselPrev')"
      @click="prev"
    >
      <outline-icon name="chevron-right" size="lg" />
    </button>

    <!-- Track -->
    <div
      ref="viewport"
      class="carousel-viewport tw-overflow-hidden"
      dir="ltr"
      :class="isMobile ? 'tw-px-0' : 'tw-px-10'"
      @touchstart.passive="onTouchStart"
      @touchmove.passive="onTouchMove"
      @touchend="onTouchEnd"
    >
      <div
        class="carousel-track tw-flex tw-transition-transform tw-duration-300 tw-ease-in-out"
        :style="trackStyle"
      >
        <article
          v-for="slide in slides"
          :key="slide.id"
          :dir="locale === 'he' ? 'rtl' : 'ltr'"
          class="exp-card tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-text-center tw-shadow-card tw-transition-all tw-duration-300 hover:tw-scale-105 hover:tw-shadow-cardHover"
          :class="{ 'exp-card--glass-mobile': isMobile }"
          :style="cardStyle"
        >
          <img
            :src="slide.image"
            :alt="slide.title"
            class="tw-mb-3 tw-h-12 tw-w-12 tw-object-contain"
          >
          <h3 class="tw-mb-1 tw-text-xl tw-font-bold tw-leading-tight tw-text-slate-900">
            {{ slide.title }}
          </h3>
          <p class="tw-text-sm tw-leading-snug tw-text-slate-600">
            {{ slide.subtitle }}
          </p>
        </article>
      </div>
    </div>

    <!-- Next arrow — hidden on mobile -->
    <button
      type="button"
      class="carousel-arrow carousel-arrow-next tw-hidden md:tw-inline-flex"
      :aria-label="$l('carouselNext')"
      @click="next"
    >
      <outline-icon name="chevron-left" size="lg" />
    </button>
  </div>
</template>

<script>
import OutlineIcon from './OutlineIcon.vue';

const AUTOPLAY_MS = 4000;
const GAP_PX = 12;
const SWIPE_THRESHOLD_PX = 40;

const SLIDE_DEFS = [
  { id: 'price', image: 'img/Explanations/price.png', titleKey: 'expPriceTitle', subtitleKey: 'expPriceSubtitle' },
  { id: 'truck', image: 'img/Explanations/truck.png', titleKey: 'expTruckTitle', subtitleKey: 'expTruckSubtitle' },
  { id: 'airplane', image: 'img/Explanations/airplane.png', titleKey: 'expAirplaneTitle', subtitleKey: 'expAirplaneSubtitle' },
  { id: 'dollar', image: 'img/Explanations/dollar.png', titleKey: 'expDollarTitle', subtitleKey: 'expDollarSubtitle' },
  { id: 'payment', image: 'img/Explanations/payment.png', titleKey: 'expPaymentTitle', subtitleKey: 'expPaymentSubtitle' },
  { id: 'sorting', image: 'img/Explanations/sorting.png', titleKey: 'expSortingTitle', subtitleKey: 'expSortingSubtitle' },
];

export default {
  name: 'Explanations',
  components: {
    OutlineIcon,
  },
  data() {
    return {
      startIndex: 0,
      visibleCount: 3,
      viewportWidth: 0,
      isMobile: false,
      timer: null,
      autoplayPaused: false,
      touchStartX: null,
      touchCurrentX: null,
      resizeObserver: null,
    };
  },
  computed: {
    slides() {
      return SLIDE_DEFS.map((slide) => ({
        id: slide.id,
        image: slide.image,
        title: this.$l(slide.titleKey),
        subtitle: this.$l(slide.subtitleKey),
      }));
    },
    maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCount);
    },
    cardWidthPx() {
      if (!this.viewportWidth || !this.visibleCount) {
        return 0;
      }
      const gaps = (this.visibleCount - 1) * GAP_PX;
      return (this.viewportWidth - gaps) / this.visibleCount;
    },
    cardStyle() {
      if (!this.cardWidthPx) {
        return {};
      }
      return {
        flex: `0 0 ${this.cardWidthPx}px`,
        width: `${this.cardWidthPx}px`,
      };
    },
    trackStyle() {
      const offset = this.startIndex * (this.cardWidthPx + GAP_PX);
      return {
        gap: `${GAP_PX}px`,
        transform: this.cardWidthPx
          ? `translateX(-${offset}px)`
          : 'none',
      };
    },
  },
  mounted() {
    this.updateVisibleCount();
    this.$nextTick(() => {
      this.updateViewportWidth();
      this.observeViewport();
    });
    window.addEventListener('resize', this.onResize);
    this.$root.$on('locale-changed', this.onLocaleChanged);
    this.startAutoplay();
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize);
    this.disconnectViewportObserver();
    this.$root.$off('locale-changed', this.onLocaleChanged);
    this.stopAutoplay();
  },
  methods: {
    onResize() {
      this.updateVisibleCount();
      this.updateViewportWidth();
    },
    updateViewportWidth() {
      const viewport = this.$refs.viewport;
      this.viewportWidth = viewport ? viewport.clientWidth : 0;
    },
    observeViewport() {
      const viewport = this.$refs.viewport;
      if (!viewport || typeof ResizeObserver === 'undefined') {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.updateViewportWidth();
      });
      this.resizeObserver.observe(viewport);
    },
    disconnectViewportObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    onLocaleChanged() {
      this.startIndex = 0;
      this.$nextTick(() => {
        this.updateVisibleCount();
        this.updateViewportWidth();
      });
    },
    updateVisibleCount() {
      const width = window.innerWidth;
      this.isMobile = width < 768;
      if (width >= 1024) {
        this.visibleCount = 3;
      } else if (width >= 768) {
        this.visibleCount = 2;
      } else {
        this.visibleCount = 1;
      }

      if (this.startIndex > this.maxIndex) {
        this.startIndex = this.maxIndex;
      }

      this.$nextTick(() => {
        this.updateViewportWidth();
      });
    },
    next(manual = true) {
      this.startIndex = this.startIndex >= this.maxIndex ? 0 : this.startIndex + 1;
      if (manual) {
        this.resetAutoplay();
      }
    },
    prev(manual = true) {
      this.startIndex = this.startIndex <= 0 ? this.maxIndex : this.startIndex - 1;
      if (manual) {
        this.resetAutoplay();
      }
    },
    startAutoplay() {
      this.stopAutoplay();
      this.timer = setInterval(() => {
        if (!this.autoplayPaused) {
          this.next(false);
        }
      }, AUTOPLAY_MS);
    },
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    resetAutoplay() {
      if (!this.autoplayPaused) {
        this.startAutoplay();
      }
    },
    pauseAutoplay() {
      this.autoplayPaused = true;
    },
    resumeAutoplay() {
      this.autoplayPaused = false;
      this.resetAutoplay();
    },
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchCurrentX = this.touchStartX;
      this.pauseAutoplay();
    },
    onTouchMove(e) {
      this.touchCurrentX = e.touches[0].clientX;
    },
    onTouchEnd() {
      if (this.touchStartX === null) {
        return;
      }
      const delta = this.touchCurrentX - this.touchStartX;
      if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
        // Positive delta = swiped right = go to previous slide
        if (delta > 0) {
          this.prev();
        } else {
          this.next();
        }
      }
      this.touchStartX = null;
      this.touchCurrentX = null;
      this.resumeAutoplay();
    },
  },
};
</script>

<style scoped>
.carousel-viewport,
.carousel-track {
  direction: ltr;
}

.carousel-viewport {
  width: 100%;
}

.carousel-track {
  width: max-content;
  will-change: transform;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  z-index: 2;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  background: #fff;
  color: #475569;
  transform: translateY(-50%);
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition: color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.carousel-arrow:hover {
  border-color: #2563eb;
  color: #2563eb;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.carousel-arrow-prev {
  right: 0;
}

.carousel-arrow-next {
  left: 0;
}

.exp-card {
  min-height: 9.5rem;
}

.exp-card--glass-mobile {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.44);
  backdrop-filter: blur(44px) saturate(165%);
  -webkit-backdrop-filter: blur(44px) saturate(165%);
  border-color: rgba(255, 255, 255, 0.78);
  box-shadow:
    0 8px 36px rgba(139, 124, 246, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -1px 0 rgba(255, 255, 255, 0.28);
}

.exp-card--glass-mobile::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    148deg,
    rgba(255, 255, 255, 0.58) 0%,
    rgba(255, 255, 255, 0.08) 42%,
    rgba(255, 255, 255, 0.32) 100%
  );
  pointer-events: none;
}

.exp-card--glass-mobile::after {
  content: '';
  position: absolute;
  top: 0;
  left: 8%;
  right: 8%;
  height: 1px;
  border-radius: 9999px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.95), transparent);
  pointer-events: none;
}
</style>
